/**
 * What every overlay in the system shares.
 *
 * Radix gives each of them the same behaviour — focus trapped inside, focus
 * returned to whatever opened it, Escape closes, the page behind stops
 * scrolling. What it does not give is a consistent *look*, and three of the
 * decisions below are the kind that drift apart one component at a time.
 */

/** The scrim behind a dialog or a drawer. */
export const SCRIM =
	// check-tokens-allow: a scrim carries no text by definition — it is the dim
	// behind the overlay, and its whole job is to let the page show through.
	"fixed inset-0 z-[var(--z-overlay)] bg-[var(--scrim)] backdrop-blur-[2px] " +
	"data-[state=open]:animate-[km-fade-in_150ms_ease-out] motion-reduce:animate-none";

/** The panel itself: a raised surface with the overlay shadow. */
export const PANEL =
	"fixed z-[var(--z-overlay)] bg-bg-surface shadow-overlay " + "focus:outline-none";

/**
 * How long an overlay takes to arrive.
 *
 * 150ms, and gone entirely under `prefers-reduced-motion`. A drawer sliding in
 * from the edge is exactly the kind of large-area movement that triggers
 * vestibular symptoms, and somebody who has asked for less of it should get a
 * panel that is simply *there* — not a slower slide.
 */
export const TRANSITION = "motion-reduce:!animate-none motion-reduce:!transition-none";

/**
 * The header's close button.
 *
 * 30px square with a border, from the design. It sits at the inline end of the
 * header, which is the right in English and the *left* in Arabic — one of the
 * few places where "the close button is in the corner" means two different
 * corners and the logical property is the only thing that gets it right.
 */
export const CLOSE_BUTTON =
	"grid size-8 shrink-0 place-items-center rounded-md border border-transparent " +
	"text-fg-muted hover:bg-bg-subtle hover:text-fg transition-colors duration-150 cursor-pointer max-sm:size-12 " +
	"outline-none focus-visible:ring-3 focus-visible:ring-ring-halo focus-visible:border-ring";
